import { Cubit } from "blac-next";
import envVariables from "src/lib/envVariables";
import { getSupportedUserLanguage } from "src/lib/i18next";
import { Language } from "src/constants/language";
import {
  Feed,
  FeedData
} from "src/ui/components/UserEducationalFeed/UserEducationalFeedBloc";

type RemoteConfigState = Record<string, never>;

interface Task {
  program: string;
  group: string;
  slug: string;
  completedAt?: string;
}

export type JourneyContentLessonItem = {
  title: string;
  type: "typeform" | "article";
  typeformId?: string;
  url?: string;
  taskSlug?: string;
};

export type JourneyContentLesson = {
  name: string;
  taskGroup?: string;
  items: JourneyContentLessonItem[];
};

export type JourneyContentClass = {
  name: string;
  subtitle: string;
  taskGroupReference: string;
  listOfLessons: JourneyContentLesson[];
};

export type JourneyContentResponse = {
  name: string;
  taskProgramReference: string;
  listOfClassesContentAvailable: JourneyContentClass[];
  type: "task-based";
  _id: string;
};

type JourneyData = {
  journey: JourneyContentResponse;
};

export class AppRemoteConfigCubit extends Cubit<RemoteConfigState> {
  apiRoot = envVariables.CMS_API_BASE_URL;

  constructor() {
    super({});
  }

  loadJourneyContent = async (
    journey: string,
    params?: {
      language?: Language;
    }
  ): Promise<JourneyData> => {
    const { language = getSupportedUserLanguage() } = params || {};
    const url = new URL(this.apiRoot);
    url.pathname = "/api/educational/program";
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify({
        language,
        journey,
        environment: envVariables.APP_ENV
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to download educational content: ${errorText}`);
    }

    const responseJson = (await response.json()) as { data: JourneyData };
    const data: JourneyData = responseJson.data;
    return data;
  };

  loadEducationalContent = async (params: {
    language?: Language;
    date?: string;
    enrolled?: string;
    tasks?: {
      inProgress?: Task[];
      completed?: Task[];
      available?: Task[];
    };
    programs?: string[];
    conditions?: string[];
    curated?: { id: string; date?: string }[];
    employerPartnerName?: string;
    payerPartnerName?: string;
    primaryCondition?: string;
  }): Promise<FeedData> => {
    const url = new URL(this.apiRoot);
    url.pathname = "/api/educational/feed";
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify(params)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to download educational content: ${errorText}`);
    }

    const responseJson = (await response.json()) as Feed;
    const data: FeedData = responseJson.data;
    return data;
  };
}
